/**
 * The scan that belongs to an account.
 *
 * Sign-up comes first, so the scan is started by a signed-in person and is
 * theirs from the first request: no token leaves the server, and the three
 * calls here carry the session the same way every other signed-in call does.
 * The server claims a finished scan by itself, whatever the site's home page
 * read as; `claimedScanId` is how the client hears that it has.
 *
 * A fourth call ties an address to the account's connected HubSpot account
 * before it is read: the pick of one of that account's websites on the
 * start page.
 *
 * All four endpoints exist only while sign-up first is on. With the switch
 * off they answer 404 NOT_FOUND to a signed-in caller, so nothing here is
 * called unless `useSignupFirst()` is true, and that 404 is never shown to a
 * person as an error.
 *
 * A refused request rejects, as every call through `api` does. The server's
 * own code for it is read with `accountScanErrorCode`.
 */
import { api } from '@/services/api'
import type { PublicScanPrepProgress } from '@/components/onboarding/PublicScanPrepScreen'

export interface AccountScanSession {
  sessionId: string
  domain: string
  /** The scan's own status, or 'claimed' once it is attached to the account. */
  status: string
  /** Null until the scan has looked. */
  platform: 'hubspot' | 'other' | null
  claimedScanId: string | null
}

export interface CurrentAccountScan {
  /**
   * The account's latest scan. Null in three cases, which look the same from
   * here: it has never started one, its claimed site was since removed, and
   * its scan was never claimed and the record of it is gone.
   */
  session: AccountScanSession | null
  hasPlan: boolean
  hasManagedPage: boolean
  ownsTeam: boolean
  /** The team's existing first domain, or null. */
  domain: string | null
  /**
   * The websites the account's connected HubSpot account serves, as bare
   * addresses, for the start page to offer. Always a list. Empty with
   * nothing connected, when HubSpot did not answer in time, and for any
   * account the start page sends on before its form: one with a site, a
   * plan, a page already set up, or a seat on somebody else's team.
   */
  portalDomains: string[]
}

/** What the public poll returns today, plus the claimed scan once there is one. */
export interface AccountScanProgress extends PublicScanPrepProgress {
  domain?: string
  currentPageUrl?: string
  errorCode?: string
  errorMessage?: string
  /**
   * Missing, not null, on the two thinner answers the server gives: for a
   * scan that a deploy killed and it started again, and for a failed scan
   * that read no pages. The second is as thin: it has no `platform`, no
   * `hubspotOnly` and no `domain`. Missing or null means "not known yet",
   * never "not on HubSpot". Nothing ends on this either way: a site read as
   * "other" is read to the end and attached like any other.
   */
  platform?: 'hubspot' | 'other' | null
  /** Missing on the same thinner answers. */
  hubspotOnly?: boolean
  claimedScanId: string | null
}

/**
 * Starts the scan. Answers 201 with the session.
 *
 * Refused with:
 * - 400 INVALID_DOMAIN: not a website address.
 * - 409 ONBOARDING_NOT_NEEDED: the account has a plan or a page already, or
 *   the person asking is not the owner of their team (a teammate has no site
 *   of their own to enter).
 * - 429 ACCOUNT_SCAN_LIMIT: five starts in a day, or two running at once.
 * - 429 DOMAIN_RATE_LIMITED: that site was scanned too often today.
 * - 403 DOMAIN_LIMIT_REACHED: the account already holds a different site.
 * - 503 ACCOUNT_SCAN_UNAVAILABLE: a read failed on the server. Try again.
 * - 409 USER_NOT_INITIALIZED: right after sign-up, before the account's own
 *   record exists. Ask again shortly.
 * - 500 ONBOARDING_ERROR: something failed that the server has no name for.
 *   Try again.
 * - 404 NOT_FOUND: the switch is off.
 */
export async function startAccountScan(domain: string): Promise<{ sessionId: string; domain: string }> {
  const response = await api.post('/onboarding/account-scan', { domain })
  return response.data.data
}

/**
 * One scan's progress, for its owner.
 *
 * A single 404 is not final. Anyone but the owner gets 404
 * SCAN_SESSION_NOT_FOUND and a scan that is gone gets 404 SCAN_NOT_FOUND,
 * and a read that fails on the server comes back as either one, because the
 * server reads a failed lookup as "no such row". The switch off is 404
 * NOT_FOUND.
 */
export async function getAccountScan(sessionId: string): Promise<AccountScanProgress> {
  const response = await api.get(`/onboarding/account-scan/${encodeURIComponent(sessionId)}`)
  return response.data.data
}

/**
 * Where the account stands, for the start page to route on.
 *
 * Refused with:
 * - 409 USER_NOT_INITIALIZED: right after sign-up, before the account's own
 *   record exists. Ask again shortly.
 * - 500 ONBOARDING_ERROR: a read failed. Try again. It does not mean the
 *   account never scanned.
 * - 404 NOT_FOUND: the switch is off.
 */
export async function getCurrentAccountScan(): Promise<CurrentAccountScan> {
  const response = await api.get('/onboarding/account-scan/current')
  return response.data.data
}

/**
 * Ties one of the connected HubSpot account's websites to that account, so
 * the report and the fix recognise it. Done before the address is read.
 *
 * Tied is one answer: 204, with nothing in it. Anything else is not a tie
 * and rejects, a 200 included: a page of HTML from something in the way
 * says "OK" too, and a read started on the strength of it would end in a
 * report that says HubSpot is not connected.
 *
 * Refused with:
 * - 400 NOT_A_PORTAL_DOMAIN: the connected HubSpot account does not list it.
 * - 403 NOT_TEAM_OWNER: only the owner of a team sets its site.
 * - 503 PORTAL_UNAVAILABLE: HubSpot could not be asked just now. Try again.
 * - 409 USER_NOT_INITIALIZED: right after sign-up. Ask again shortly.
 * - 500 ONBOARDING_ERROR: something failed that the server has no name for.
 * - 404 NOT_FOUND: the switch is off.
 * Each but the last carries a sentence for the person, read with
 * `accountScanErrorMessage`. The server can take several seconds to answer:
 * it waits on HubSpot.
 */
export async function chooseAccountPortalDomain(domain: string): Promise<void> {
  const response = await api.post('/onboarding/account-scan/portal-domain', { domain })
  if (response?.status !== 204) throw new Error('The picked address was not answered as tied')
}

/**
 * The server's own code for a refused request ('ACCOUNT_SCAN_LIMIT' and the
 * like), or null. Null too for an answer that is not the server's own: a
 * network failure, a plain-text rate limit, a page of HTML.
 */
export function accountScanErrorCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { error?: unknown } } } | null)?.response?.data?.error
  return typeof code === 'string' ? code : null
}

/**
 * The sentence the server wrote for a refused request, or null. Only off a
 * refusal that is the server's own, which always has a code beside the
 * sentence: a proxy's "Bad Gateway" and an error's own message are for a
 * developer and are never shown to a person.
 */
export function accountScanErrorMessage(error: unknown): string | null {
  if (accountScanErrorCode(error) === null) return null
  const message = (error as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message
  return typeof message === 'string' && message.trim() ? message.trim() : null
}

/** The report for a claimed scan. */
export function buildReportPath(claimedScanId: string, domain: string | null | undefined): string {
  const params = new URLSearchParams({ scanId: claimedScanId })
  if (domain) params.set('domain', domain)
  return `/welcome/results?${params.toString()}`
}

/** The page that waits on a scan. */
export function buildReadingPath(sessionId: string): string {
  return `/welcome/reading/${encodeURIComponent(sessionId)}`
}

/**
 * Still being read, or read and waiting to be attached to the account,
 * whatever its home page read as: a site read as not on HubSpot is attached
 * like any other. A scan that failed or ran out is over: there is nothing
 * to wait for and the start page asks again.
 */
export function isStillReading(session: AccountScanSession): boolean {
  if (session.claimedScanId) return false
  if (session.status === 'pending' || session.status === 'scanning') return true
  return session.status === 'completed' || session.status === 'partial'
}
